/**
 * 应用版本管理 API
 */
import type {
  AppVersion,
  UpdateInfo,
  VersionCheckRequest,
  VersionListQuery,
  VersionListResponse,
  GrayDeviceWhitelist,
} from '@xiaozhi/shared-types';

const API_BASE = '/api/app-version';

/**
 * 检查版本更新（公开接口）
 */
export async function checkAppUpdate(request: VersionCheckRequest): Promise<UpdateInfo> {
  const params = new URLSearchParams({
    version: request.version,
    versionCode: request.versionCode.toString(),
    deviceId: request.deviceId,
    ...(request.channel && { channel: request.channel }),
    ...(request.osVersion && { osVersion: request.osVersion }),
  });

  const response = await fetch(`${API_BASE}/check?${params}`);
  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '检查更新失败');
  }

  return data.data;
}

/**
 * 获取版本列表
 */
export async function getVersionList(query: VersionListQuery): Promise<VersionListResponse> {
  const params = new URLSearchParams();
  
  if (query.platform) params.append('platform', query.platform);
  if (query.channel) params.append('channel', query.channel);
  if (query.status !== undefined) params.append('status', query.status.toString());
  if (query.page) params.append('page', query.page.toString());
  if (query.pageSize) params.append('pageSize', query.pageSize.toString());

  const response = await fetch(`${API_BASE}/list?${params}`);
  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '获取版本列表失败');
  }

  return data.data;
}

/**
 * 获取版本详情
 */
export async function getVersionById(id: number): Promise<AppVersion> {
  const response = await fetch(`${API_BASE}/${id}`);
  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '获取版本详情失败');
  }

  return data.data;
}

/**
 * 创建新版本
 */
export async function createVersion(formData: FormData): Promise<AppVersion> {
  const response = await fetch(`${API_BASE}`, {
    method: 'POST',
    body: formData,
  });

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '创建版本失败');
  }

  return data.data;
}

/**
 * 更新版本信息
 */
export async function updateVersion(id: number, formData: FormData): Promise<AppVersion> {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'PUT',
    body: formData,
  });

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '更新版本失败');
  }

  return data.data;
}

/**
 * 删除版本
 */
export async function deleteVersion(id: number): Promise<void> {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'DELETE',
  });

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '删除版本失败');
  }
}

/**
 * 获取版本白名单
 */
export async function getVersionWhitelist(versionId: number): Promise<GrayDeviceWhitelist[]> {
  const response = await fetch(`${API_BASE}/${versionId}/whitelist`);
  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '获取白名单失败');
  }

  return data.data;
}

/**
 * 添加白名单设备
 */
export async function addWhitelistDevices(versionId: number, deviceIds: string[]): Promise<void> {
  const response = await fetch(`${API_BASE}/${versionId}/whitelist`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ deviceIds }),
  });

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '添加白名单失败');
  }
}

/**
 * 删除白名单设备
 */
export async function removeWhitelistDevice(versionId: number, deviceId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/${versionId}/whitelist/${deviceId}`, {
    method: 'DELETE',
  });

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '删除白名单设备失败');
  }
}

/**
 * 获取更新统计
 */
export async function getUpdateStats(startDate?: Date, endDate?: Date): Promise<{
  totalChecks: number;
  totalDownloads: number;
  totalInstalls: number;
  successRate: number;
}> {
  const params = new URLSearchParams();
  
  if (startDate) params.append('startDate', startDate.toISOString());
  if (endDate) params.append('endDate', endDate.toISOString());

  const response = await fetch(`${API_BASE}/stats/summary?${params}`);
  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || '获取统计数据失败');
  }

  return data.data;
}

/**
 * 获取APK下载地址
 */
export function getApkDownloadUrl(filename: string): string {
  return `${API_BASE}/download/${filename}`;
}

